import React from "react";
import Navbar from "../../components/shared/Navbar";
import Footer from "../../components/shared/Footer";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { Info, Repeat, Lock } from "lucide-react";
import NavbarWeb from "../../components/NavbarWeb";

const features = [
  {
    icon: <Info className="w-6 h-6 text-green-600" />,
    title: "Accuracy Matters",
    description:
      "Doctors often don’t have your full medical history, which makes it harder to be accurate",
  },
  {
    icon: <Repeat className="w-6 h-6 text-green-600" />,
    title: "Info Transfer Challenges",
    description:
      "Every time you visit a new doctor, your past records don’t always follow, and details get missed.",
  },
  {
    icon: <Lock className="w-6 h-6 text-green-600" />,
    title: "Stay in Control and Updated",
    description:
      "You're in charge of your own documentations at the grasp of your fingertips with constant updates and analysis after each visit.",
  },
];

function Landing() {
  const [activeStep, setActiveStep] = React.useState(1);

  return (
    <>
      <NavbarWeb />
      {/* Hero Section */}
      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-green-200 via-white to-green-100 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-10 w-40 h-40 bg-green-100 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-300 rounded-full blur-3xl"></div>
        </div>
        {/* Hero Section */}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 md:px-8 max-w-6xl mx-auto relative z-10">
          <div className="mt-5 mb-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6">
              <span className="text-gray-800">YOUR</span>{" "}
              <span className="text-green-600">MEDICAL HISTORY</span>
              <br />
              <span className="text-gray-800">ONE TAP AWAY</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Secure, instant access to your complete medical records.
              Empowering better healthcare decisions for you and your providers.
            </p>
          </div>

          {/* Video Placeholder */}
          <div className="w-full max-w-4xl mx-auto mb-8">
            <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                  <Play className="w-6 h-6 text-green-600 ml-1 group-hover:scale-110 transition-transform" />
                </div>
              </div>
              {/* Subtle overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </div>

          {/* Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Link
              to="/login"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 min-w-[140px]"
            >
              Log In
            </Link>
            <Link
              to="/sign-up"
              className="bg-white hover:bg-gray-50 border-2 border-green-600 text-green-600 hover:text-green-700 px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 min-w-[140px]"
            >
              Sign Up
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-col sm:flex-row items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Consulted Doctors</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>24/7 Access</span>
            </div>
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
      {/* Steps Section */}
      <div className="p-6 md:p-8">
        <h3 className="text-center text-sm text-green-600 font-semibold">
          HOW IT WORKS
        </h3>
        <h2 className="text-center text-xl md:text-2xl font-bold text-gray-800 mt-2">
          Get Started with 5 Easy Steps
        </h2>

        {/* Mobile Layout */}
        <div className="mt-8 md:hidden">
          <div className="grid grid-cols-2 gap-4 justify-items-center">
            {[1, 2, 3, 4, 5].map((step, index) => (
              <div
                key={step}
                onClick={() => setActiveStep(step)}
                className={`p-4 rounded-md shadow-sm w-full max-w-[160px] cursor-pointer transition-all duration-300 ${
                  activeStep === step ? "bg-green-100" : "opacity-50"
                }`}
              >
                <h4 className="text-[22px] font-bold text-[#1F300A]">
                  0{step}
                </h4>
                <h5 className="text-[13px] font-semibold text-[#1F300A] mt-1 leading-tight">
                  {
                    [
                      "Upload Medical History Reports",
                      "AI-Driven Analysis of Your Data",
                      "HIPAA-Compliant Cloud Storage",
                      "Add Your MedTag to your Apple Wallet",
                      "Share Info with your Medical Provider",
                    ][index]
                  }
                </h5>
              </div>
            ))}
          </div>

          {/* Image comes after steps */}
          <div className="mt-6 bg-[#D9D9D9] w-full h-[180px] rounded-[20px]" />
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-2 gap-8 mt-10">
          {/* Left: Step cards wrapped in flex */}
          <div className="flex gap-6 flex-wrap justify-center">
            {[1, 2, 3, 4, 5].map((step, index) => (
              <div
                key={step}
                onClick={() => setActiveStep(step)}
                className={`p-4 rounded-md shadow-sm w-[230px] cursor-pointer transition-all duration-300 ${
                  activeStep === step ? "bg-green-100" : "opacity-50"
                }`}
              >
                <h4 className="text-[40px] font-bold text-[#1F300A]">
                  0{step}
                </h4>
                <h5 className="text-[15px] font-semibold text-[#1F300A] mt-1">
                  {
                    [
                      "Upload Medical History Reports",
                      "AI-Driven Analysis of Your Data",
                      "HIPAA-Compliant Cloud Storage",
                      "Add Your MedTag to your Apple Wallet",
                      "Share Information with your Medical Provider",
                    ][index]
                  }
                </h5>
                <p className="text-[12px] text-[#1F300A] mt-1 leading-snug">
                  {
                    [
                      "Keep all your medical records organized in one spot.",
                      "AI-driven summary of all your medical documents.",
                      "Secure cloud storage ensures all your medical records remain safe.",
                      "Easily access your digital health record anytime, anywhere.",
                      "Allows seamless data sharing with medical professionals.",
                    ][index]
                  }
                </p>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div className="bg-[#D9D9D9] w-full h-auto min-h-[320px] rounded-[26px]" />
        </div>
      </div>

      {/* WHY Section */}
      <section className="py-12 bg-white">
        <h2 className="text-[#1D7D4B] text-3xl font-[900] mb-8 text-center">
          WHY?
        </h2>
        <div className="flex flex-wrap justify-center gap-6 px-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-[#74FF7933] p-6 w-full max-w-[330px] rounded-lg"
            >
              <div className="flex items-center gap-3 mb-2">
                {feature.icon}
                <h3 className="text-[#25995C] text-lg font-bold">
                  {feature.title}
                </h3>
              </div>
              <p className="text-[#25995C] text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Banner */}
      <div className="flex justify-center items-center py-12">
        <div className="relative bg-gradient-to-b from-green-200 to-white rounded-2xl shadow-lg p-10 text-center w-[90%] md:w-[80%]">
          <h1 className="text-[28px] md:text-[36px] lg:text-[50px] font-bold text-[#315304]">
            Take Control of Your
          </h1>
          <h2 className="text-[28px] md:text-[36px] lg:text-[50px] font-bold text-[#30953099]">
            Health Records Today!
          </h2>
          <p className="text-[12px] text-[#1F300A] mt-2">
            Access, update, and analyze your health records anytime, right at
            your fingertips. <br /> Stay informed and in control after every
            visit, because your health matters.
          </p>
          <Link to="/sign-up">
            <button className="mt-9 px-6 py-2 bg-gradient-to-t from-[#d7edcb] to-[#f9fcf7] text-[#385613] font-semibold rounded-lg shadow-md transition">
              JOIN NOW
            </button>
          </Link>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default Landing;
